import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Tooltip, Popover } from '@app/ui';
import './ActionButtons.scss';
import { useSmpContext } from './SmpContext';
import {
    CloseOutlined,
    FileAddOutlined,
    DownloadOutlined,
    ExportOutlined,
    SettingOutlined,
    UnorderedListOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';

const ActionButtons = () => {
    const { t } = useTranslation('measurement');
    const {
        isSelectMode,
        isPremiumCollections,
        openSelectionMode,
        openDownloadModal,
        downloadJpegs,
        isDownloadingJpegs,
        openCollectionsModal,
        closeSelectionMode,
        exportCsv,
        selectionType,
        selectedCount,
        isNonenSelected,
        isAllSelected,
        selectAll,
        unselectAll,
        view,
        setView,
        isSelectingColumns,
        setIsSelectingColumns,
        setColumns,
        tableColumns,
        hasCollabPostsReporting,
    } = useSmpContext();

    const [columnState, setColumnState] = useState({
        ...tableColumns,
        showCollabPostInfo: tableColumns.showCollabPostInfo ?? false,
    });

    const isListView = view === 'list';

    const SettingsPopover = (
        <div className="adjust-table-popover">
            <div style={{ fontWeight: 600, paddingBottom: 8 }}>
                <SettingOutlined style={{ color: '#009FDF' }} />{' '}
                {t('socialMediaExposure.tableSettings.title')}
            </div>
            <div style={{ paddingBottom: 8, fontFamily: 'pnregular' }}>
                <Checkbox
                    checked={columnState.postLevelMetrics}
                    onChange={e =>
                        setColumnState(prev => ({
                            ...prev,
                            postLevelMetrics: e.target.checked,
                        }))
                    }
                    style={{ fontFamily: 'pnregular' }}
                >
                    {t('socialMediaExposure.tableSettings.postLevelMetrics')}
                </Checkbox>
            </div>
            <div style={{ paddingBottom: 8, fontFamily: 'pnregular' }}>
                <Checkbox
                    checked={columnState.organicAndPaidMetrics}
                    onChange={e =>
                        setColumnState(prev => ({
                            ...prev,
                            organicAndPaidMetrics: e.target.checked,
                        }))
                    }
                    style={{ fontFamily: 'pnregular' }}
                >
                    {t(
                        'socialMediaExposure.tableSettings.paidVsOrganicMetrics',
                    )}
                </Checkbox>
            </div>
            {hasCollabPostsReporting && (
                <div
                    style={{
                        paddingBottom: 8,
                        fontFamily: 'pnregular',
                    }}
                >
                    <Checkbox
                        checked={columnState.showCollabPostInfo ?? false}
                        onChange={e =>
                            setColumnState(prev => ({
                                ...prev,
                                showCollabPostInfo: e.target.checked,
                            }))
                        }
                        style={{ fontFamily: 'pnregular' }}
                    >
                        {t(
                            'socialMediaExposure.tableSettings.showCollabPostInfo',
                        )}
                    </Checkbox>
                </div>
            )}
            <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
                <Button
                    size="small"
                    onClick={() => setIsSelectingColumns(false)}
                    style={{ fontFamily: 'pnregular', color: 'black' }}
                >
                    {t('socialMediaExposure.actions.cancel')}
                </Button>
                <Button
                    type="primary"
                    size="small"
                    style={{ fontFamily: 'pnregular' }}
                    onClick={() => setColumns(columnState)}
                >
                    {t('socialMediaExposure.actions.apply')}
                </Button>
            </div>
        </div>
    );

    return (
        <div className="action-buttons-container-v2">
            <div className="left-actions">
                {isSelectMode ? (
                    <>
                        <Button
                            color="default"
                            variant="solid"
                            onClick={closeSelectionMode}
                            className="action-button"
                            icon={<CloseOutlined />}
                            style={{ fontFamily: 'pnregular' }}
                            data-test="cancel-select-mode-btn"
                        >
                            {t('socialMediaExposure.actions.cancel')}
                        </Button>

                        <Button
                            color="primary"
                            variant="outlined"
                            onClick={selectAll}
                            disabled={isAllSelected}
                            style={{ fontFamily: 'pnregular', color: 'black' }}
                            data-test="select-all-posts-btn"
                        >
                            <Checkbox checked={isAllSelected} />{' '}
                            {t('socialMediaExposure.actions.selectAll')}
                        </Button>

                        <Button
                            color="default"
                            variant="solid"
                            onClick={unselectAll}
                            disabled={isNonenSelected}
                            className="action-button"
                            style={{ fontFamily: 'pnregular' }}
                            data-test="deselect-all-posts-btn"
                        >
                            <input type="checkbox" readOnly checked={false} />{' '}
                            {t('socialMediaExposure.actions.deselectAll')}
                        </Button>

                        {selectionType === 'assign' && isPremiumCollections && (
                            <Button
                                type="primary"
                                onClick={openCollectionsModal}
                                disabled={isNonenSelected}
                                className="action-button"
                                icon={<i className="fas fa-folder-open" />}
                                style={{ fontFamily: 'pnregular' }}
                                data-test="assign-to-collection-btn"
                            >
                                {t(
                                    'socialMediaExposure.actions.assignToCollection',
                                )}{' '}
                                - ({selectedCount})
                            </Button>
                        )}

                        {selectionType === 'download' && (
                            <Button
                                type="primary"
                                onClick={openDownloadModal}
                                disabled={isNonenSelected}
                                className="action-button"
                                icon={<i className="fas fa-download" />}
                                style={{ fontFamily: 'pnregular' }}
                            >
                                {t('socialMediaExposure.actions.downloadPdfs')}{' '}
                                ({selectedCount})
                            </Button>
                        )}

                        {selectionType === 'download' && (
                            <Button
                                type="primary"
                                onClick={downloadJpegs}
                                loading={isDownloadingJpegs}
                                disabled={isNonenSelected}
                                className="action-button"
                                icon={<i className="fas fa-download" />}
                                style={{ fontFamily: 'pnregular' }}
                                data-test="download-jpegs"
                            >
                                {t('socialMediaExposure.actions.downloadJpegs')}{' '}
                                ({selectedCount})
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        {isPremiumCollections && (
                            <Button
                                type="primary"
                                onClick={() => openSelectionMode('assign')}
                                className="action-button-v2"
                                style={{ fontFamily: 'pnregular' }}
                                icon={<FileAddOutlined />}
                                data-test="activate-select-mode-assign"
                            >
                                {t('socialMediaExposure.actions.assignPosts')}
                            </Button>
                        )}

                        <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => openSelectionMode('download')}
                            className="action-button-v2"
                            style={{ fontFamily: 'pnregular' }}
                            icon={<DownloadOutlined />}
                            data-test="activate-select-mode-export"
                        >
                            {t('socialMediaExposure.actions.downloadImages')}
                        </Button>

                        <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => exportCsv()}
                            className="action-button-v2"
                            style={{ fontFamily: 'pnregular' }}
                            icon={<ExportOutlined />}
                            data-test="export-csv-btn"
                        >
                            {t('socialMediaExposure.actions.exportToCsv')}
                        </Button>
                    </>
                )}
            </div>
            <div className="right-icons">
                {isListView && (
                    <Tooltip title={t('socialMediaExposure.views.settings')}>
                        <Popover
                            content={SettingsPopover}
                            trigger="click"
                            open={isSelectingColumns}
                            onOpenChange={open => {
                                if (!open) setIsSelectingColumns(false);
                            }}
                            placement="topRight"
                        >
                            <Button
                                color="primary"
                                variant="outlined"
                                icon={<SettingOutlined />}
                                className="action-icon-button"
                                onClick={() => {
                                    setColumnState(tableColumns);
                                    setIsSelectingColumns(true);
                                }}
                            />
                        </Popover>
                    </Tooltip>
                )}
                <Tooltip title={t('socialMediaExposure.views.listView')}>
                    <Button
                        color="primary"
                        variant={isListView ? 'solid' : 'outlined'}
                        icon={<UnorderedListOutlined />}
                        className="action-icon-button"
                        onClick={() => setView('list')}
                        data-test="list-view-btn"
                    />
                </Tooltip>
                <Tooltip title={t('socialMediaExposure.views.gridView')}>
                    <Button
                        color="primary"
                        variant={isListView ? 'outlined' : 'solid'}
                        icon={<AppstoreOutlined />}
                        className="action-icon-button"
                        onClick={() => setView('grid')}
                        data-test="grid-view-btn"
                    />
                </Tooltip>
            </div>
        </div>
    );
};

export default ActionButtons;
